// ═══════════════════════════════════════════════════════════════
// V·IANet Governance — Gobernanza (M1 Structure)
// ═══════════════════════════════════════════════════════════════

import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useI18n } from '../../../../i18n';
import { AppHeader } from '../../../shared/components/AppHeader';
import { LoadingSpinner } from '../../../shared/components/LoadingSpinner';
import { C } from '../../../../lib/design';
import {
  fetchVacantes,
  fetchMatrizResponsabilidad,
  fetchAnalisisImpacto,
  fetchPuestos,
  fetchOrganosGobierno,
  type OrganoGobierno,
  type MiembroOrganoGobierno,
} from '../../../shared/services/api';

type TabKey = 'vacantes' | 'matriz' | 'impacto' | 'organos';

// ═══════════════════════════════════════════════════════════════
// VACANTES TAB
// ═══════════════════════════════════════════════════════════════
function VacantesTab() {
  const { t } = useI18n();
  const navigate = useNavigate();
  const [vacantes, setVacantes] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchVacantes()
      .then((r) => setVacantes(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <Loading />;

  return (
    <div>
      <div style={{ fontSize: 13, color: '#5A6171', marginBottom: 16 }}>
        {t.structure.governance.vacanciesCount(vacantes.length)}
      </div>
      {vacantes.length === 0 ? (
        <EmptyState text={t.structure.governance.noVacancies} />
      ) : (
        <table style={tableStyle}>
          <thead>
            <tr>
              <th style={thStyle}>{t.structure.puestoLabel}</th>
              <th style={thStyle}>{t.structure.codigoLabel}</th>
              <th style={thStyle}>{t.structure.nivelLabel}</th>
              <th style={thStyle}>{t.structure.departamentoLabel}</th>
              <th style={thStyle}>{t.structure.organizacionLabel}</th>
              <th style={thStyle}>{t.structure.governance.actions ?? 'Acciones'}</th>
            </tr>
          </thead>
          <tbody>
            {vacantes.map((v: any) => (
              <tr key={v.id} style={trStyle}>
                <td style={{ ...tdStyle, fontWeight: 600 }}>{v.titulo}</td>
                <td style={tdStyle}>{v.codigo || '—'}</td>
                <td style={tdStyle}>
                  <span
                    style={{
                      padding: '2px 6px',
                      borderRadius: 4,
                      fontSize: 10,
                      fontWeight: 600,
                      background: '#F3F4F6',
                    }}
                  >
                    {v.nivel?.replace(/_/g, ' ')}
                  </span>
                </td>
                <td style={tdStyle}>{v.departamento?.nombre || '—'}</td>
                <td style={tdStyle}>
                  {v.departamento?.organizacion?.nombre || v.organizacion?.nombre || '—'}
                </td>
                <td style={tdStyle}>
                  <button
                    onClick={() => navigate(`/structure?focusPuesto=${v.id}&tab=organigrama`)}
                    style={{
                      padding: '4px 10px',
                      border: '1px solid #2F5496',
                      borderRadius: 6,
                      background: 'transparent',
                      color: '#2F5496',
                      fontSize: 11,
                      fontWeight: 600,
                      cursor: 'pointer',
                      fontFamily: 'inherit',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {t.structure.governance.viewInOrgChart ?? 'Ver en organigrama'}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// MATRIZ DE RESPONSABILIDAD TAB
// ═══════════════════════════════════════════════════════════════
function MatrizTab() {
  const { t } = useI18n();
  const [matriz, setMatriz] = useState<any>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchMatrizResponsabilidad()
      .then((r) => setMatriz(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <Loading />;

  if (!matriz || !matriz.procesos || !matriz.puestos) {
    return <EmptyState text={t.structure.governance.errorLoadMatrix} />;
  }

  const procesos: any[] = matriz.procesos ?? [];
  const puestos: any[] = matriz.puestos ?? [];
  const celdas: Record<string, string> = matriz.celdas ?? {};

  if (procesos.length === 0 || puestos.length === 0) {
    return <EmptyState text={t.structure.governance.noDataMatrix} />;
  }

  return (
    <div style={{ overflowX: 'auto' }}>
      <div style={{ fontSize: 13, color: '#5A6171', marginBottom: 16 }}>
        {t.structure.governance.matrixDesc(procesos.length, puestos.length)}
      </div>
      <table style={{ ...tableStyle, fontSize: 11 }}>
        <thead>
          <tr>
            <th style={{ ...thStyle, position: 'sticky', left: 0, zIndex: 2, minWidth: 200 }}>
              {t.structure.governance.processCol}
            </th>
            {puestos.map((p: any) => (
              <th
                key={p.id}
                style={{ ...thStyle, minWidth: 100, textAlign: 'center', fontSize: 9 }}
              >
                {p.titulo}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {procesos.map((proc: any) => {
            const procId = proc.id ?? proc.codigo;
            const hasOwner = puestos.some(
              (puesto: any) => celdas[`${procId}_${puesto.id}`] === 'P',
            );
            return (
              <tr key={procId} style={trStyle}>
                <td
                  style={{
                    ...tdStyle,
                    position: 'sticky',
                    left: 0,
                    background: '#fff',
                    fontWeight: 600,
                    fontSize: 11,
                    zIndex: 1,
                  }}
                  title={hasOwner ? undefined : 'Sin responsable de proceso'}
                >
                  {!hasOwner && <span style={{ color: '#DC2626', marginRight: 4 }}>●</span>}
                  <span style={{ color: C.gray400, marginRight: 6 }}>{proc.codigo}</span>
                  {proc.nombre}
                </td>
                {puestos.map((puesto: any) => {
                  const key = `${proc.id ?? proc.codigo}_${puesto.id}`;
                  const rol = celdas[key];
                  return (
                    <td key={puesto.id} style={{ ...tdStyle, textAlign: 'center' }}>
                      {rol ? (
                        <span
                          style={{
                            display: 'inline-block',
                            width: 22,
                            height: 22,
                            borderRadius: '50%',
                            lineHeight: '22px',
                            textAlign: 'center',
                            fontSize: 10,
                            fontWeight: 700,
                            background:
                              rol === 'P'
                                ? 'var(--color-primario)'
                                : rol === 'R'
                                  ? 'var(--color-secundario)'
                                  : '#E2E6ED',
                            color: rol === 'P' || rol === 'R' ? '#fff' : C.gray700,
                          }}
                        >
                          {rol}
                        </span>
                      ) : null}
                    </td>
                  );
                })}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// ANALISIS DE IMPACTO TAB
// ═══════════════════════════════════════════════════════════════
function ImpactoTab() {
  const { t } = useI18n();
  const [allPuestos, setAllPuestos] = useState<any[]>([]);
  const [selectedPuesto, setSelectedPuesto] = useState('');
  const [impacto, setImpacto] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [loadingPuestos, setLoadingPuestos] = useState(true);

  useEffect(() => {
    fetchPuestos({ porPagina: '500' })
      .then((r) => setAllPuestos(r.data))
      .catch(() => {})
      .finally(() => setLoadingPuestos(false));
  }, []);

  const handleAnalyze = useCallback(async () => {
    if (!selectedPuesto) return;
    setLoading(true);
    try {
      const res = await fetchAnalisisImpacto(selectedPuesto);
      setImpacto(res.data);
    } catch (err) {
      console.error('Error:', err);
      setImpacto(null);
    } finally {
      setLoading(false);
    }
  }, [selectedPuesto]);

  return (
    <div>
      <div style={{ display: 'flex', gap: 12, marginBottom: 20, alignItems: 'flex-end' }}>
        <div style={{ flex: 1 }}>
          <label
            style={{
              display: 'block',
              fontSize: 12,
              fontWeight: 600,
              color: C.gray700,
              marginBottom: 4,
            }}
          >
            {t.structure.governance.selectPuesto}
          </label>
          <select
            value={selectedPuesto}
            onChange={(e) => setSelectedPuesto(e.target.value)}
            disabled={loadingPuestos}
            style={{
              width: '100%',
              padding: '8px 12px',
              border: '1px solid #D1D5DB',
              borderRadius: 6,
              fontSize: 13,
              fontFamily: 'inherit',
              outline: 'none',
            }}
          >
            <option value="">{t.structure.governance.selectPuestoPlaceholder}</option>
            {allPuestos.map((p: any) => (
              <option key={p.id} value={p.id}>
                {p.titulo} {p.departamento ? `(${p.departamento.nombre})` : ''}
              </option>
            ))}
          </select>
        </div>
        <button
          onClick={handleAnalyze}
          disabled={!selectedPuesto || loading}
          style={{
            padding: '8px 20px',
            border: 'none',
            borderRadius: 6,
            background: selectedPuesto ? 'var(--color-secundario)' : '#D1D5DB',
            color: '#fff',
            cursor: selectedPuesto ? 'pointer' : 'default',
            fontSize: 13,
            fontWeight: 600,
            fontFamily: 'inherit',
          }}
        >
          {loading ? t.structure.governance.analyzing : t.structure.governance.analyzeImpact}
        </button>
      </div>

      {impacto && (
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
            gap: 16,
          }}
        >
          {/* Position info */}
          <ImpactCard title={t.structure.governance.analyzedPuesto}>
            <InfoRow label={t.structure.governance.titulo} value={impacto.puesto?.titulo} />
            <InfoRow
              label={t.structure.nivelLabel}
              value={impacto.puesto?.nivel?.replace(/_/g, ' ')}
            />
            <InfoRow
              label={t.structure.departamentoLabel}
              value={impacto.puesto?.departamento?.nombre}
            />
            <InfoRow
              label={t.structure.governance.persona}
              value={
                impacto.persona
                  ? `${impacto.persona.nombre} ${impacto.persona.apellidos}`
                  : t.structure.vacantLabel
              }
            />
          </ImpactCard>

          {/* Subordinates */}
          <ImpactCard
            title={`${t.structure.governance.directSubordinates(impacto.subordinadosDirectos?.length ?? 0)}${impacto.resumen?.totalSubordinados != null ? ` · Total (${impacto.resumen.totalSubordinados})` : ''}`}
          >
            {impacto.resumen?.nivelRiesgo && (
              <div style={{ marginBottom: 8 }}>
                <span
                  style={{
                    display: 'inline-block',
                    padding: '2px 8px',
                    borderRadius: 12,
                    fontSize: 11,
                    fontWeight: 600,
                    background:
                      impacto.resumen.nivelRiesgo === 'ALTO'
                        ? '#FEF2F2'
                        : impacto.resumen.nivelRiesgo === 'MEDIO'
                          ? '#FFF7ED'
                          : '#F0FDF4',
                    color:
                      impacto.resumen.nivelRiesgo === 'ALTO'
                        ? '#DC2626'
                        : impacto.resumen.nivelRiesgo === 'MEDIO'
                          ? '#D97706'
                          : '#16A34A',
                  }}
                >
                  {impacto.resumen.nivelRiesgo}
                </span>
              </div>
            )}
            {impacto.subordinadosDirectos?.length > 0 ? (
              impacto.subordinadosDirectos.map((s: any) => (
                <div key={s.id} style={{ fontSize: 12, color: C.gray700, marginBottom: 4 }}>
                  {s.titulo}
                  {s.persona && <span style={{ color: C.gray400 }}> — {s.persona.nombre}</span>}
                </div>
              ))
            ) : (
              <div style={{ fontSize: 12, color: C.gray400, fontStyle: 'italic' }}>
                {t.structure.governance.noSubordinates}
              </div>
            )}
          </ImpactCard>

          {/* Processes affected */}
          <ImpactCard
            title={t.structure.governance.affectedProcesses(impacto.procesosAfectados?.length ?? 0)}
          >
            {impacto.procesosAfectados?.length > 0 ? (
              impacto.procesosAfectados.map((p: any) => (
                <div
                  key={p.id ?? p.codigo}
                  style={{ fontSize: 12, color: C.gray700, marginBottom: 4 }}
                >
                  <span style={{ color: C.gray400, marginRight: 4 }}>{p.codigo}</span>
                  {p.nombre}
                  {p.rol && (
                    <span style={{ fontSize: 10, color: 'var(--color-secundario)', marginLeft: 6 }}>
                      ({p.rol})
                    </span>
                  )}
                </div>
              ))
            ) : (
              <div style={{ fontSize: 12, color: C.gray400, fontStyle: 'italic' }}>
                {t.structure.governance.noProcesses}
              </div>
            )}
          </ImpactCard>

          {/* Documents affected */}
          <ImpactCard
            title={t.structure.governance.affectedDocuments(
              impacto.documentosAfectados?.length ?? 0,
            )}
          >
            {impacto.documentosAfectados?.length > 0 ? (
              impacto.documentosAfectados.map((d: any) => (
                <div
                  key={d.id ?? d.codigo}
                  style={{ fontSize: 12, color: C.gray700, marginBottom: 4 }}
                >
                  <span style={{ color: C.gray400, marginRight: 4 }}>{d.codigo}</span>
                  {d.titulo}
                </div>
              ))
            ) : (
              <div style={{ fontSize: 12, color: C.gray400, fontStyle: 'italic' }}>
                {t.structure.governance.noDocuments}
              </div>
            )}
          </ImpactCard>

          {/* Governance roles */}
          <ImpactCard
            title={t.structure.governance.governanceRoles(impacto.rolesGobernanza?.length ?? 0)}
          >
            {impacto.rolesGobernanza?.length > 0 ? (
              impacto.rolesGobernanza.map((r: any, i: number) => (
                <div key={i} style={{ fontSize: 12, color: C.gray700, marginBottom: 4 }}>
                  {r.tipoRol?.replace(/_/g, ' ')}
                  {r.ambito && <span style={{ color: C.gray400 }}> — {r.ambito}</span>}
                </div>
              ))
            ) : (
              <div style={{ fontSize: 12, color: C.gray400, fontStyle: 'italic' }}>
                {t.structure.governance.noGovernanceRoles}
              </div>
            )}
          </ImpactCard>
        </div>
      )}

      {!impacto && !loading && <EmptyState text={t.structure.governance.impactHint} />}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// ÓRGANOS TAB (F2.10)
// ═══════════════════════════════════════════════════════════════
function OrganosTab() {
  const { t } = useI18n();
  const [organos, setOrganos] = useState<OrganoGobierno[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        setError('');
        const res = await fetchOrganosGobierno();
        if (!cancelled) setOrganos(res.data ?? []);
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : t.structure.governance.organosErrorLoad);
          setOrganos([]);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [t.structure.governance.organosErrorLoad]);

  if (loading) return <Loading />;

  if (error) {
    return (
      <div
        style={{
          background: '#FEF2F2',
          border: '1px solid #FECACA',
          borderRadius: 8,
          padding: 16,
          color: '#B91C1C',
          fontSize: 13,
        }}
      >
        {error}
      </div>
    );
  }

  if (organos.length === 0) {
    return <EmptyState text={t.structure.governance.organosEmpty} />;
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
      {organos.map((organo) => (
        <OrganoCard key={organo.id} organo={organo} />
      ))}
    </div>
  );
}

function OrganoCard({ organo }: { organo: OrganoGobierno }) {
  const { t } = useI18n();
  const miembrosActivos = organo.miembros.filter((m) => !m.fechaCese);
  const miembrosOrdenados = [...organo.miembros].sort((a, b) => {
    // Activos primero, luego cesados por fecha desc
    if (!a.fechaCese && b.fechaCese) return -1;
    if (a.fechaCese && !b.fechaCese) return 1;
    return b.fechaNombramiento.localeCompare(a.fechaNombramiento);
  });

  return (
    <div
      style={{
        background: '#fff',
        border: '1px solid #E2E6ED',
        borderRadius: 10,
        padding: '18px 22px',
        boxShadow: '0 1px 3px rgba(15,23,42,0.05)',
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          gap: 12,
          flexWrap: 'wrap',
          marginBottom: organo.descripcion ? 8 : 12,
        }}
      >
        <div style={{ flex: 1, minWidth: 0 }}>
          <h3
            style={{
              margin: 0,
              fontSize: 16,
              fontWeight: 700,
              color: '#0E243E',
              lineHeight: 1.3,
            }}
          >
            {organo.nombre}
          </h3>
          <div
            style={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: 6,
              marginTop: 6,
              alignItems: 'center',
            }}
          >
            <span
              style={{
                padding: '2px 8px',
                borderRadius: 6,
                background: '#EFF6FF',
                color: '#2F5496',
                fontSize: 11,
                fontWeight: 600,
              }}
            >
              {organo.tipoOrgano}
            </span>
            <span
              style={{
                padding: '2px 8px',
                borderRadius: 6,
                background: '#F5F3FF',
                color: '#7C3AED',
                fontSize: 11,
                fontWeight: 600,
              }}
              title={organo.entidad.codigo}
            >
              {organo.entidad.razonSocial}
            </span>
          </div>
        </div>
        <div
          style={{
            fontSize: 11,
            color: '#5A6171',
            fontWeight: 600,
            whiteSpace: 'nowrap',
          }}
        >
          {t.structure.governance.miembrosActivos(miembrosActivos.length)}
        </div>
      </div>

      {organo.descripcion && (
        <p
          style={{
            margin: '0 0 12px',
            fontSize: 13,
            color: '#374151',
            lineHeight: 1.5,
            whiteSpace: 'pre-wrap',
          }}
        >
          {organo.descripcion}
        </p>
      )}

      {organo.miembros.length === 0 ? (
        <div
          style={{
            padding: '16px',
            textAlign: 'center',
            color: '#94A3B8',
            fontSize: 12,
            background: '#F8FAFC',
            borderRadius: 6,
            border: '1px dashed #E2E6ED',
          }}
        >
          {t.structure.governance.sinMiembros}
        </div>
      ) : (
        <table
          style={{
            width: '100%',
            borderCollapse: 'collapse',
            fontSize: 12,
            border: '1px solid #E5E7EB',
            borderRadius: 6,
            overflow: 'hidden',
          }}
        >
          <thead>
            <tr style={{ background: '#F8FAFC' }}>
              <th style={miembroThStyle}>{t.structure.governance.nombreCol}</th>
              <th style={miembroThStyle}>{t.structure.governance.cargoCol}</th>
              <th style={miembroThStyle}>{t.structure.governance.desdeCol}</th>
              <th style={miembroThStyle}>{t.structure.governance.hastaCol}</th>
              <th style={{ ...miembroThStyle, textAlign: 'center', width: 90 }}>
                {t.structure.governance.estadoCol}
              </th>
            </tr>
          </thead>
          <tbody>
            {miembrosOrdenados.map((miembro) => (
              <MiembroRow key={miembro.id} miembro={miembro} />
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

function MiembroRow({ miembro }: { miembro: MiembroOrganoGobierno }) {
  const { t } = useI18n();
  const isActivo = !miembro.fechaCese;
  const nombreCompleto = miembro.persona
    ? `${miembro.persona.nombre} ${miembro.persona.apellidos}`.trim()
    : (miembro.nombreExterno ?? '—');

  return (
    <tr style={{ borderTop: '1px solid #F3F4F6' }}>
      <td style={miembroTdStyle}>
        <span style={{ fontWeight: 600, color: '#0E243E' }}>{nombreCompleto}</span>
        {!miembro.persona && miembro.nombreExterno && (
          <span
            style={{
              marginLeft: 6,
              fontSize: 10,
              color: '#94A3B8',
              fontWeight: 500,
            }}
          >
            ({t.structure.governance.estadoCol === 'Status' ? 'external' : 'externo'})
          </span>
        )}
      </td>
      <td style={{ ...miembroTdStyle, color: '#5A6171' }}>{miembro.cargo}</td>
      <td style={{ ...miembroTdStyle, color: '#5A6171', fontVariantNumeric: 'tabular-nums' }}>
        {miembro.fechaNombramiento?.slice(0, 10) ?? '—'}
      </td>
      <td style={{ ...miembroTdStyle, color: '#5A6171', fontVariantNumeric: 'tabular-nums' }}>
        {miembro.fechaCese?.slice(0, 10) ?? '—'}
      </td>
      <td style={{ ...miembroTdStyle, textAlign: 'center' }}>
        <span
          style={{
            display: 'inline-block',
            padding: '2px 8px',
            borderRadius: 10,
            fontSize: 10,
            fontWeight: 600,
            background: isActivo ? '#F0FDF4' : '#F3F4F6',
            color: isActivo ? '#16A34A' : '#6B7280',
            whiteSpace: 'nowrap',
          }}
        >
          {isActivo ? t.structure.governance.estadoActivo : t.structure.governance.estadoCesado}
        </span>
      </td>
    </tr>
  );
}

const miembroThStyle: React.CSSProperties = {
  padding: '8px 12px',
  textAlign: 'left',
  fontSize: 10,
  fontWeight: 700,
  color: '#5A6171',
  textTransform: 'uppercase',
  letterSpacing: 0.3,
};

const miembroTdStyle: React.CSSProperties = {
  padding: '8px 12px',
  fontSize: 12,
};

// ─── Shared Components ───
function ImpactCard({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div
      style={{
        background: '#fff',
        border: '1px solid #E2E6ED',
        borderRadius: 8,
        padding: 16,
        boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
      }}
    >
      <div
        style={{
          fontSize: 12,
          fontWeight: 700,
          color: 'var(--color-primario)',
          marginBottom: 10,
          textTransform: 'uppercase',
          letterSpacing: 0.5,
        }}
      >
        {title}
      </div>
      {children}
    </div>
  );
}

function InfoRow({ label, value }: { label: string; value?: string | null }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
      <span style={{ fontSize: 12, color: '#5A6171' }}>{label}</span>
      <span style={{ fontSize: 12, fontWeight: 500, color: '#1A1D23' }}>{value ?? '—'}</span>
    </div>
  );
}

function Loading() {
  const { t } = useI18n();
  return (
    <div style={{ textAlign: 'center', padding: 40 }}>
      <LoadingSpinner text={t.common.cargando} size="sm" />
    </div>
  );
}

function EmptyState({ text }: { text: string }) {
  return (
    <div style={{ textAlign: 'center', padding: 40, color: C.gray400, fontSize: 13 }}>{text}</div>
  );
}

// ═══════════════════════════════════════════════════════════════
// MAIN PAGE
// ═══════════════════════════════════════════════════════════════
export function GobernanzaPage({ embedded = false }: { embedded?: boolean }) {
  const { t } = useI18n();
  const [tab, setTab] = useState<TabKey>('vacantes');

  const tabs: { key: TabKey; label: string }[] = [
    { key: 'vacantes', label: t.structure.governance.vacanciesTab },
    { key: 'matriz', label: t.structure.governance.matrixTab },
    { key: 'impacto', label: t.structure.governance.impactTab },
    { key: 'organos', label: t.structure.governance.organosTab },
  ];

  return (
    <div style={{ background: 'var(--wl-background, #ECEEF2)', minHeight: '100%' }}>
      {!embedded && <AppHeader modulo="Structure" titulo={t.structure.governance.title} />}
      <div style={{ padding: '20px 32px', maxWidth: 1400, margin: '0 auto' }}>
        {/* Tabs */}
        <div
          style={{ display: 'flex', gap: 0, borderBottom: '2px solid #E2E6ED', marginBottom: 24 }}
        >
          {tabs.map((tabItem) => (
            <button
              key={tabItem.key}
              onClick={() => setTab(tabItem.key)}
              style={{
                padding: '10px 24px',
                border: 'none',
                background: 'transparent',
                borderBottom:
                  tab === tabItem.key ? '2px solid var(--color-primario)' : '2px solid transparent',
                color: tab === tabItem.key ? 'var(--color-primario)' : '#5A6171',
                fontWeight: tab === tabItem.key ? 600 : 400,
                fontSize: 14,
                cursor: 'pointer',
                fontFamily: 'inherit',
                marginBottom: -2,
              }}
            >
              {tabItem.label}
            </button>
          ))}
        </div>

        {/* Tab content */}
        {tab === 'vacantes' && <VacantesTab />}
        {tab === 'matriz' && <MatrizTab />}
        {tab === 'impacto' && <ImpactoTab />}
        {tab === 'organos' && <OrganosTab />}
      </div>
    </div>
  );
}

// ─── Shared Styles ───
const tableStyle: React.CSSProperties = {
  width: '100%',
  borderCollapse: 'collapse',
  background: '#fff',
  borderRadius: 8,
  overflow: 'hidden',
  boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
};
const thStyle: React.CSSProperties = {
  padding: '10px 16px',
  textAlign: 'left',
  fontSize: 11,
  fontWeight: 700,
  color: '#fff',
  background: '#0E243E',
  textTransform: 'uppercase',
  letterSpacing: 0.5,
};
const tdStyle: React.CSSProperties = {
  padding: '10px 16px',
  fontSize: 13,
  color: C.gray700,
  borderBottom: '1px solid #F2F4F6',
};
const trStyle: React.CSSProperties = {};
